import React, { useEffect, useMemo, useState } from "react";
import { Dimensions, Image, Linking, Platform, Text, TouchableOpacity, View } from "react-native";
import { ButtonTypeDLS, ButtonWidthTypeToken, ColorTokens, FontFamilyTokens, FontSizeTokens, IconSizeTokens, IconTokens, TextAlignTokens, TypographyTypeDLS, UploadFileStatus } from "@voltmoney/schema";
import { ModalAlignmentEnum } from '@voltmoney/types';
import { Typography } from "../../typography/Typography";
import BoilerPlate from "../boilerPlate/BoilerPlate";
import CustomHeader from "../../customHeader/CustomHeader";
import { Button } from "../../button/Button";
import { Icon } from "../../icon/Icon";
import { isMobile, isMobileView } from "../../stack/utils";
import { UploadFile } from "../../UploadFile/UploadFile";
import BottomSheet from "../BottomSheet";
import { DOCUMENT_TYPE } from "../asESViewSampleDocument/types";
import { getScreenType, isFromiOSBrowser } from "../utils";
const ASESIndividualDocument = ({ height, triggerAction, goBackAction, uploadFormAction, continueAction, downloadFormAction, documentDetails, viewSampleDocumentAction, downloadDocumentLoading, docDownloadDetails, viewUploadedFile, documentUploadFailed }) => {
    const [showUploadedView, setShowUploadedView] = useState(false);
    const [showDownloadForm, setShowDownloadForm] = useState(true);
    const [uploadFileDetails, setUploadFileDetails] = useState(undefined);
    const [fileURL, setFileURL] = useState('');
    const [uploadFileLimitExceeded, setUploadFileLimitExceeded] = useState(false);
    const [halfCardHeight, setHalfCardHeight] = useState(0);
    const [continueClicked, setContinueClicked] = useState(false);
    const [fileIncorrectFormat, setFileIncorrectFormat] = useState(false);
    const [errorHalfCardClosed, setErrorHalfCardClosed] = useState(false);
    const [deleteFileClicked, setDeleteFileClicked] = useState(false);
    const [documentDetail, setDocumentDetail] = useState(documentDetails);
    const [showContinue, setShowContinue] = useState(false);
    const windowHeight = Dimensions.get('window').height;
    useEffect(() => {
        setShowDownloadForm(documentDetail?.documentType !== DOCUMENT_TYPE.PAN_CARD && documentDetail?.documentType !== DOCUMENT_TYPE.AADHAAR_CARD);
        return () => {
            setDocumentDetail(null);
        };
    }, []);
    useEffect(() => {
        if (documentDetails?.status === "PENDING" && showUploadedView && !deleteFileClicked) {
            setShowContinue(true);
        }
        else if (documentDetails?.status === "UPLOADED" && !deleteFileClicked) {
            setShowContinue(true);
        }
        else {
            setShowContinue(false);
        }
    }, [showUploadedView, documentDetails?.status, deleteFileClicked]);
    useEffect(() => {
        if (uploadFileLimitExceeded) {
            setHalfCardHeight(isMobileView() ? 310 : 250);
        }
        else if (fileIncorrectFormat) {
            setHalfCardHeight(isMobileView ? 270 : 250);
        }
        else if (documentUploadFailed) {
            setHalfCardHeight(isMobileView() ? 320 : 270);
        }
    }, [uploadFileLimitExceeded, fileIncorrectFormat, documentUploadFailed]);
    const headerView = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement(CustomHeader, { title: "", triggerAction: triggerAction, hideNeedHelp: true, goBackAction: goBackAction })));
    };
    const getDescription = (type) => {
        let description;
        switch (type) {
            case DOCUMENT_TYPE.CO_BORROWER_FORM:
                description = "Download, fill out, and upload the signed co-applicant form to proceed";
                break;
            case DOCUMENT_TYPE.ADDENDUM:
                description = "Download and upload the signed Addendum with the required signatures";
                break;
            case DOCUMENT_TYPE.PAN_CARD:
                description = "Print, self-attest with signature and date, and upload your PAN card below";
                break;
            case DOCUMENT_TYPE.AADHAAR_CARD:
                description = "Print, self-attest with signature and date, and upload the Aadhaar card below";
                break;
            case DOCUMENT_TYPE.ECS_MANDATE:
                description = "Download and upload the signed mandate form to proceed.";
                break;
        }
        return description;
    };
    const getImportantNotesData = (type) => {
        let firstPoint;
        let secondPoint;
        switch (type) {
            case DOCUMENT_TYPE.PAN_CARD:
                firstPoint = "PAN card needs to be printed and signed with date as described in the sample above.";
                secondPoint = "Please upload a clear copy of the document to avoid rejection.";
                break;
            case DOCUMENT_TYPE.AADHAAR_CARD:
                firstPoint = "Aadhaar card needs to be printed and signed with date as described in the sample above.";
                secondPoint = "Please upload a clear copy of the document to avoid rejection.";
                break;
            case DOCUMENT_TYPE.ADDENDUM:
                firstPoint = "Sign against your name in the form as described in the sample above";
                secondPoint = "Upload a clear document with the required signatures to proceed";
                break;
            case DOCUMENT_TYPE.CO_BORROWER_FORM:
                firstPoint = "Stick your passport size photo and sign on top of it to attest the document";
                secondPoint = isMobileView() ? "Upload a clear photo of the document\nto proceed" : "Upload a clear photo of the document to proceed";
                break;
            case DOCUMENT_TYPE.ECS_MANDATE:
                firstPoint = "Mandate form needs to be signed to automatically debit your interest from your bank account.";
                secondPoint = isMobileView() ? "Upload a clear photo of the document\nto proceed" : "Please sign on the dotted line as described in the same and upload a clear PDF file.";
                break;
        }
        return { firstPoint, secondPoint };
    };
    useEffect(() => {
        if (uploadFileDetails !== undefined) {
            setShowUploadedView(true);
            if ((uploadFileDetails.size || uploadFileDetails?.fileSize) > 5 * 1024 * 1024) {
                setUploadFileLimitExceeded(true);
            }
            else if (!uploadFileDetails.type.includes("pdf") && !uploadFileDetails.type.includes("jpeg")) {
                setFileIncorrectFormat(true);
                setUploadFileLimitExceeded(false);
            }
            else {
                setFileIncorrectFormat(false);
                setUploadFileLimitExceeded(false);
                triggerAction({
                    ...uploadFormAction,
                    payload: {
                        fileDetails: uploadFileDetails,
                        uploadPreSignedURL: docDownloadDetails?.uploadUrl
                    }
                });
            }
        }
        else {
            setShowUploadedView(false);
        }
    }, [fileURL, uploadFileDetails]);
    const renderFileIncorrectFormat = () => {
        return (React.createElement(View, { style: { alignContent: 'center', alignItems: 'center', padding: 16 } },
            React.createElement(Icon, { name: IconTokens.ErrorIcon, size: IconSizeTokens.XXXXXXL }),
            React.createElement(View, { style: { marginTop: 16 } },
                React.createElement(Typography, { label: "Unsupported file format", typeDLS: TypographyTypeDLS.H3S, color: ColorTokens.Grey_Night })),
            React.createElement(View, { style: {
                    borderRadius: 8,
                    padding: 8,
                    backgroundColor: "#FEF4E6",
                    marginTop: 24,
                    marginBottom: 24,
                    width: '100%'
                } },
                React.createElement(Typography, { label: "Please upload a jpeg or PDF to proceed", textAlign: TextAlignTokens.center, typeDLS: TypographyTypeDLS.B1R, color: ColorTokens.Grey_Night })),
            React.createElement(Button, { label: "Retry document upload", typeDLS: ButtonTypeDLS.SECONDARY, width: ButtonWidthTypeToken.FULL, onPress: () => {
                    setContinueClicked(false);
                } })));
    };
    const RenderUploadView = useMemo(() => {
        return (React.createElement(UploadFile, { onDeleteFile: () => {
                setUploadFileDetails(undefined);
                setDeleteFileClicked(true);
                setFileIncorrectFormat(false);
                setUploadFileLimitExceeded(false);
            }, setUploadFileURL: (fileUrl, uploadDetails) => {
                setUploadFileDetails(uploadDetails);
                setFileURL(fileUrl);
                setDeleteFileClicked(false);
            }, errorMessage: '', message: "Supported file format of max. size 5 MB - jpeg, pdf", isFromASESDocumentsFlow: true, viewUploadedFile: viewUploadedFile, triggerAction: triggerAction, errorHalfCardClosed: errorHalfCardClosed, showDocUploaded: documentDetail?.status === "UPLOADED", existingFileURLASES: docDownloadDetails?.existingFileURL, fileName: documentDetail?.documentDisplayName, documentType: documentDetail?.documentType, status: documentDetail?.status === "UPLOADED" ? UploadFileStatus.SUCCESS : UploadFileStatus.DEFAULT }));
    }, [errorHalfCardClosed, deleteFileClicked]);
    const imageURL = (type) => {
        let imageUrl;
        switch (type) {
            case DOCUMENT_TYPE.AADHAAR_CARD:
                imageUrl = "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/sample_aadhar.png";
                break;
            case DOCUMENT_TYPE.PAN_CARD:
                imageUrl = "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/sample_pan_doc.png";
                break;
            case DOCUMENT_TYPE.CO_BORROWER_FORM:
                imageUrl = "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/co_borrower_form_sample.pdf";
                break;
            case DOCUMENT_TYPE.ADDENDUM:
                imageUrl = "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/addendum_sample_doc.pdf";
                break;
            case DOCUMENT_TYPE.ECS_MANDATE:
                imageUrl = "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/sample_mandate_form.png";
        }
        return imageUrl;
    };
    const bodyView = () => {
        const { firstPoint, secondPoint } = getImportantNotesData(documentDetail?.documentType);
        return (React.createElement(View, { style: {
                marginTop: 16,
                paddingHorizontal: 16,
                height: isMobileView()
                    ? isFromiOSBrowser()
                        ? height + 35
                        : windowHeight > 600 ? height + 100 : height - 45
                    : windowHeight > 1000 ? height - 90 : windowHeight > 600 ? height + 100 : height - 80
            } },
            React.createElement(Typography, { label: documentDetail?.documentDisplayName, fontSize: FontSizeTokens.XL, lineHeight: 28, color: ColorTokens.Grey_Night, fontWeight: "600", numberOfLines: 2, fontFamily: FontFamilyTokens.Poppins }),
            React.createElement(View, { style: { marginTop: 4 } },
                React.createElement(Typography, { label: getDescription(documentDetail?.documentType), typeDLS: TypographyTypeDLS.B1R, color: ColorTokens.Grey_Charcoal })),
            React.createElement(View, null, showDownloadForm ? React.createElement(View, { style: { marginTop: 24 } },
                React.createElement(Typography, { typeDLS: TypographyTypeDLS.B0M, color: ColorTokens.Grey_Charcoal, label: "Step 1" }),
                React.createElement(View, { style: { marginTop: 8 } },
                    React.createElement(Button, { typeDLS: ButtonTypeDLS.SECONDARY, width: ButtonWidthTypeToken.FULL, label: "Download document ", iconAlignment: 'right', iconName: IconTokens.DOWNLOAD_DOCUMENT, onPress: () => {
                            triggerAction({
                                ...downloadFormAction,
                                payload: {
                                    downloadURL: docDownloadDetails?.downloadUrl,
                                    documentType: documentDetails?.documentType,
                                    documentDisplayName: documentDetails?.documentDisplayName
                                }
                            });
                        }, loading: downloadDocumentLoading })))
                :
                    React.createElement(React.Fragment, null)),
            React.createElement(View, { style: { marginTop: 24 } },
                React.createElement(Typography, { typeDLS: TypographyTypeDLS.B0M, color: ColorTokens.Grey_Charcoal, label: showDownloadForm ? "Step 2" : "Step 1" }),
                React.createElement(TouchableOpacity, { activeOpacity: 1, style: {
                        marginTop: 8,
                        borderRadius: 8,
                        backgroundColor: "#F3F5FC",
                        paddingVertical: 4,
                        paddingLeft: 8,
                        paddingRight: 12,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignContent: 'center',
                        alignItems: 'center'
                    }, onPress: () => {
                        isMobileView() ? Linking.openURL(imageURL(documentDetail?.documentType))
                            :
                                triggerAction({
                                    ...viewSampleDocumentAction,
                                    payload: {
                                        documentType: documentDetail?.documentType
                                    }
                                });
                    } },
                    React.createElement(View, { style: { flexDirection: 'row', alignContent: 'center', alignItems: 'center' } },
                        React.createElement(Image, { source: { uri: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/preview_pdf.png" }, style: { width: 32, height: 32 } }),
                        React.createElement(View, { style: { marginLeft: 12 } },
                            React.createElement(Typography, { typeDLS: TypographyTypeDLS.B0S, label: "View sample document", color: ColorTokens.Primary_100 }))),
                    React.createElement(Icon, { name: IconTokens.RightBlueArrow, size: IconSizeTokens.XL }))),
            React.createElement(View, { style: {
                    marginTop: 8,
                    paddingVertical: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    backgroundColor: '#F3F5FC'
                } },
                React.createElement(View, { style: { flexDirection: 'row', alignContent: 'center', alignItems: 'center' } },
                    React.createElement(Image, { source: { uri: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/glowing_bulb.png" }, style: { width: 16, height: 22 } }),
                    React.createElement(View, { style: { marginLeft: 12 } },
                        React.createElement(Typography, { label: "Important note", typeDLS: TypographyTypeDLS.B2S, color: ColorTokens.Grey_Night }))),
                React.createElement(View, { style: { marginTop: 8, marginLeft: 28 } },
                    React.createElement(View, { style: {
                            marginBottom: 8,
                            flexDirection: 'row',
                            alignContent: 'center',
                            alignItems: 'center',
                        } },
                        React.createElement(Text, { style: {
                                fontSize: 4,
                                marginRight: 4,
                                fontFamily: 'Inter',
                                color: 'black',
                                marginBottom: isMobileView() ?
                                    firstPoint.length > 55
                                        ? 16
                                        : 0
                                    : firstPoint.length > 70
                                        ? 16
                                        : 0
                            } }, '\u2B24'),
                        React.createElement(Typography, { label: firstPoint, typeDLS: TypographyTypeDLS.B2R, color: ColorTokens.Grey_Night })),
                    React.createElement(View, { style: { flexDirection: 'row', alignContent: 'center', alignItems: 'center' } },
                        React.createElement(Text, { style: {
                                fontSize: 4,
                                marginRight: 4,
                                color: 'black',
                                marginBottom: isMobileView() ? secondPoint.length > 45 ? 16 : 0 : secondPoint.length > 65 ? 16 : 0
                            } }, '\u2B24'),
                        React.createElement(Typography, { label: secondPoint, typeDLS: TypographyTypeDLS.B2R, color: ColorTokens.Grey_Night })))),
            React.createElement(View, { style: { marginTop: 24, paddingBottom: 16 } },
                React.createElement(Typography, { typeDLS: TypographyTypeDLS.B0M, color: ColorTokens.Grey_Charcoal, label: showDownloadForm ? "Step 3" : "Step 2" }),
                React.createElement(View, { style: { marginTop: 8, marginBottom: 8 } }, RenderUploadView),
                React.createElement(View, null, showContinue ?
                    React.createElement(View, { style: { marginBottom: 8 } },
                        React.createElement(Button, { typeDLS: ButtonTypeDLS.PRIMARY, width: ButtonWidthTypeToken.FULL, label: "Continue", onPress: () => {
                                setContinueClicked(true);
                                setErrorHalfCardClosed(false);
                                if (!uploadFileLimitExceeded && !fileIncorrectFormat && !documentUploadFailed) {
                                    triggerAction({
                                        ...continueAction,
                                        payload: {
                                            documentId: documentDetail?.documentId
                                        }
                                    });
                                }
                            } })) : React.createElement(React.Fragment, null)),
                React.createElement(Typography, { label: "Supported file format of max. size 5 MB - jpeg, pdf", typeDLS: TypographyTypeDLS.B2R, color: ColorTokens.Grey_Charcoal }))));
    };
    const renderFileLimitExceeded = () => {
        return (React.createElement(View, { style: { alignContent: 'center', alignItems: 'center', padding: 16 } },
            React.createElement(Icon, { name: IconTokens.ErrorIcon, size: IconSizeTokens.XXXXXXL }),
            React.createElement(View, { style: { marginTop: 16 } },
                React.createElement(Typography, { label: isMobileView() ? "Document size is greater than\n5 MB" : "Document size is greater than 5 MB", typeDLS: TypographyTypeDLS.H3S, color: ColorTokens.Grey_Night, textAlign: TextAlignTokens.center })),
            React.createElement(View, { style: {
                    borderRadius: 8,
                    padding: 8,
                    backgroundColor: "#FEF4E6",
                    marginTop: 24,
                    marginBottom: 24,
                    width: '100%'
                } },
                React.createElement(Typography, { label: "File size exceeds the 5 MB limit. Please select a smaller file.", textAlign: TextAlignTokens.center, typeDLS: TypographyTypeDLS.B1R, color: ColorTokens.Grey_Night })),
            React.createElement(Button, { label: "Retry document upload", typeDLS: ButtonTypeDLS.SECONDARY, width: ButtonWidthTypeToken.FULL, onPress: () => {
                    setContinueClicked(false);
                } })));
    };
    const renderFileNotUploadedCase = () => {
        return (React.createElement(View, { style: { alignContent: 'center', alignItems: 'center', padding: 16 } },
            React.createElement(Icon, { name: IconTokens.ErrorIcon, size: IconSizeTokens.XXXXXXL }),
            React.createElement(View, { style: { marginTop: 16 } },
                React.createElement(Typography, { label: "Document upload failed", typeDLS: TypographyTypeDLS.H3S, color: ColorTokens.Grey_Night })),
            React.createElement(View, { style: {
                    borderRadius: 8,
                    padding: 8,
                    backgroundColor: "#FEF4E6",
                    marginTop: 24,
                    marginBottom: 24,
                    width: '100%'
                } },
                React.createElement(Typography, { label: "Document upload failed due to a technical error. Please upload documents again to proceed", textAlign: TextAlignTokens.center, typeDLS: TypographyTypeDLS.B1R, color: ColorTokens.Grey_Night })),
            React.createElement(Button, { label: "Retry document upload", typeDLS: ButtonTypeDLS.SECONDARY, width: ButtonWidthTypeToken.FULL, onPress: () => {
                    setContinueClicked(false);
                } })));
    };
    const renderCasesModal = () => {
        if (uploadFileLimitExceeded)
            return renderFileLimitExceeded();
        else if (documentUploadFailed)
            return renderFileNotUploadedCase();
        else if (fileIncorrectFormat)
            return renderFileIncorrectFormat();
    };
    return (React.createElement(View, { style: { marginHorizontal: -16 } },
        React.createElement(BoilerPlate, { height: height - 16, headerChild: React.createElement(View, { style: { height: Platform.OS === "ios" ? 64 : 55 } }, headerView()), bottomChildren: React.createElement(React.Fragment, null) }, React.createElement(React.Fragment, null, bodyView())),
        continueClicked && (uploadFileLimitExceeded || documentUploadFailed || fileIncorrectFormat) ?
            React.createElement(View, { style: { position: 'absolute', bottom: 0, flex: 1 } },
                React.createElement(BottomSheet, { autoAdjustHeight: false, height: halfCardHeight, width: '100%', closeFunction: () => setContinueClicked(false), alignment: ModalAlignmentEnum.BOTTOM, backgroundColor: 'transparent', hasDraggableIcon: false, sheetBackgroundColor: 'white', isDismissible: uploadFileLimitExceeded || fileIncorrectFormat || documentUploadFailed, radius: 4, screenHeight: isMobile(getScreenType(Dimensions.get('window').width)) ? Platform.OS !== "ios" ? Dimensions.get('window').height : Dimensions.get('window').height + 5 : height, screenWidth: isMobile(getScreenType(Dimensions.get('window').width)) ? Dimensions.get('window').width : 508 }, renderCasesModal())) : React.createElement(React.Fragment, null)));
};
export default (ASESIndividualDocument);
